<template>
  <v-container fluid class="checkout-bg pa-0" style="min-height: 100vh;">
    <v-row justify="center" class="ma-0 py-10">
      <v-col cols="12" sm="9" md="6" lg="5">

        <!-- Checkout card -->
        <div class="checkout-card">

          <!-- Card header -->
          <div class="checkout-header">
            <div>
              <p class="checkout-header__tag">Compra segura</p>
              <h2 class="checkout-header__title">
                {{ productService ? productService.name : 'Producto / Servicio' }}
              </h2>
              <p v-if="organizationName" class="checkout-header__org mb-0">
                Vendido por {{ organizationName }}
              </p>
            </div>
            <div class="d-flex flex-column align-end">
              <v-img
                src="@/assets/images/weChamber/logo-WC-microsite.svg"
                max-width="30"
                contain
                class="checkout-header__logo mb-1"
              />
              <span class="checkout-header__secure">
                <v-icon x-small style="color:rgba(255,255,255,0.55)">mdi-lock</v-icon>
                <span>Pago seguro</span>
              </span>
            </div>
          </div>

          <!-- Step indicator -->
          <div v-if="!loading && !errorMessage && step < 4" class="step-track">
            <div
              v-for="(label, i) in stepLabels"
              :key="i"
              class="step-item"
              :class="[
                step === i + 1 ? 'step-active' : step > i + 1 ? 'step-done' : 'step-idle',
                i < stepLabels.length - 1 ? 'step-item--has-connector' : '',
                step > i + 1 ? 'step-item--connector-done' : '',
              ]"
            >
              <div class="step-circle">
                <v-icon v-if="step > i + 1" x-small color="white">mdi-check</v-icon>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="step-label">{{ label }}</span>
            </div>
          </div>

          <!-- Card body -->
          <div class="checkout-body">

            <!-- Loading -->
            <div v-if="loading" class="text-center py-12">
              <v-progress-circular indeterminate size="44" color="#1b273e" width="3" />
              <p class="body-2 mt-4" style="color:#6b7280">Cargando información del producto...</p>
            </div>

            <!-- Error al cargar -->
            <v-alert v-else-if="errorMessage" type="error" outlined dense class="body-2">
              {{ errorMessage }}
            </v-alert>

            <template v-else-if="productService">

              <!-- ── STEP 1: Resumen / Cantidad ── -->
              <div v-if="step === 1">
                <!-- Tarjeta de producto: thumbnail + detalle + precio -->
                <div class="product-card mb-5">
                  <v-img
                    v-if="productService.bannerImage"
                    :src="productService.bannerImage"
                    class="product-card__thumb flex-shrink-0"
                    max-width="88"
                    max-height="88"
                    aspect-ratio="1"
                    cover
                  />
                  <div
                    v-else
                    class="product-card__thumb product-card__thumb--placeholder d-flex align-center justify-center flex-shrink-0"
                  >
                    <v-icon size="32" color="#9ca3af">mdi-image-outline</v-icon>
                  </div>

                  <div class="product-card__body">
                    <p class="product-card__name mb-1">{{ productService.name }}</p>
                    <p v-if="productService.shortDescription" class="product-card__desc mb-0">
                      {{ productService.shortDescription }}
                    </p>
                    <p v-if="productService.maxQuantityPerOrder > 1" class="product-card__max-qty mt-2 mb-0">
                      <v-icon x-small color="#9ca3af">mdi-information-outline</v-icon>
                      Máx. {{ productService.maxQuantityPerOrder }} por compra
                    </p>
                  </div>

                  <div class="product-card__pricing flex-shrink-0">
                    <p class="product-card__price-label mb-0">Precio</p>
                    <p class="product-card__price mb-0">${{ formatAmount(productService.price) }}</p>
                    <p class="product-card__currency mb-0">{{ productService.currency || 'MXN' }}</p>
                  </div>
                </div>

                <p class="section-title mb-3">Cantidad</p>
                <div class="qty-control mb-5">
                  <v-btn icon outlined color="#1b273e" :disabled="quantity <= 1" @click="quantity--">
                    <v-icon>mdi-minus</v-icon>
                  </v-btn>
                  <div class="qty-control__value">{{ quantity }}</div>
                  <v-btn
                    icon
                    outlined
                    color="#1b273e"
                    :disabled="quantity >= (productService.maxQuantityPerOrder || 1)"
                    @click="quantity++"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>

                <div class="totals-box">
                  <div class="totals-box__row">
                    <span>Subtotal</span>
                    <span>${{ formatAmount(subtotal) }} {{ productService.currency || 'MXN' }}</span>
                  </div>
                  <div class="totals-box__row totals-box__row--total">
                    <span>Total</span>
                    <span class="totals-box__amount">
                      ${{ formatAmount(subtotal) }}
                      <span class="totals-box__currency">{{ productService.currency || 'MXN' }}</span>
                    </span>
                  </div>
                </div>

                <div class="step-actions step-actions--end mt-6">
                  <v-btn depressed class="btn-primary" :loading="stepLoading" @click="onConfirmQuantity">
                    Continuar <v-icon right small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- ── STEP 2: Datos del comprador ── -->
              <div v-else-if="step === 2">
                <p class="section-title mb-1">Tus datos</p>
                <p class="section-text mb-4">
                  Usaremos estos datos para enviarte el recibo y, si aplica, la factura.
                </p>

                <v-form ref="buyerForm" v-model="buyerFormValid">
                  <v-text-field
                    v-model="buyer.name"
                    label="Nombre(s) *"
                    outlined
                    dense
                    rounded
                    :rules="[requiredRule]"
                    prepend-inner-icon="mdi-account"
                  />
                  <v-text-field
                    v-model="buyer.lastName"
                    label="Apellidos *"
                    outlined
                    dense
                    rounded
                    :rules="[requiredRule]"
                    prepend-inner-icon="mdi-account-outline"
                  />
                  <v-text-field
                    v-model="buyer.email"
                    label="Correo electrónico *"
                    outlined
                    dense
                    rounded
                    :rules="[requiredRule, emailRule]"
                    prepend-inner-icon="mdi-email"
                  />
                  <v-text-field
                    v-model="buyer.phone"
                    label="Teléfono"
                    outlined
                    dense
                    rounded
                    prepend-inner-icon="mdi-phone"
                  />
                  <v-switch
                    v-model="buyer.needsInvoice"
                    color="#1b273e"
                    label="Requiero factura"
                    hide-details
                    class="mt-0"
                  />
                </v-form>

                <div class="step-actions mt-6">
                  <v-btn text class="btn-back" @click="step = 1">
                    <v-icon left small>mdi-arrow-left</v-icon> Regresar
                  </v-btn>
                  <v-btn
                    depressed
                    class="btn-primary"
                    :loading="stepLoading"
                    :disabled="!buyerFormValid"
                    @click="onConfirmBuyer"
                  >
                    Continuar <v-icon right small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- ── STEP 3: Payment Element ── -->
              <div v-else-if="step === 3">
                <p class="section-title mb-1">Método de pago</p>
                <p class="section-text mb-4">
                  Selecciona cómo quieres completar tu compra.
                </p>

                <v-radio-group
                  v-model="paymentMethod"
                  row
                  class="mt-0 mb-4"
                  hide-details
                  @change="selectPaymentMethod"
                >
                  <v-radio value="CREDIT_CARD" label="Tarjeta / otros métodos" />
                  <v-radio
                    v-if="showAsocoinsPayment"
                    value="ASOCOINS"
                    label="Asocoins"
                    color="#1b273e"
                  />
                </v-radio-group>

                <v-alert v-if="paymentMethod === 'ASOCOINS'" type="info" outlined dense class="mb-4">
                  Usarás <strong>{{ formatAsocoins(asocoinsEligibility.requiredAmount) }} Asocoins</strong>
                  para cubrir el total de la orden. El pago no se combina con cupones.
                </v-alert>

                <!-- Loader mientras Stripe inicializa -->
                <div v-if="paymentMethod !== 'ASOCOINS' && paymentElementLoading" class="text-center py-6">
                  <v-progress-circular indeterminate color="#1b273e" size="36" />
                  <p class="caption mt-3" style="color:#4a5568">Cargando formulario de pago…</p>
                </div>

                <!-- Contenedor del Payment Element -->
                <div
                  v-if="paymentMethod !== 'ASOCOINS'"
                  ref="paymentElementContainer"
                  :class="{ 'd-none': paymentElementLoading }"
                  class="payment-element-wrapper mb-4"
                />

                <v-alert v-if="paymentError" type="error" outlined dense class="caption mt-3">
                  {{ paymentError }}
                </v-alert>

                <!-- Resumen de orden -->
                <div class="confirm-summary mt-4">
                  <div class="confirm-summary__row">
                    <span class="confirm-summary__label">Producto</span>
                    <span class="confirm-summary__value">{{ productService.name }}</span>
                  </div>
                  <div class="confirm-summary__row">
                    <span class="confirm-summary__label">Cantidad</span>
                    <span class="confirm-summary__value">{{ quantity }}</span>
                  </div>
                  <div class="confirm-summary__row confirm-summary__row--total">
                    <span class="confirm-summary__label">Total</span>
                    <span class="confirm-summary__total">
                      ${{ formatAmount(subtotal) }}
                      <span class="confirm-summary__currency">{{ productService.currency || 'MXN' }}</span>
                    </span>
                  </div>
                </div>

                <div class="step-actions mt-6">
                  <v-btn text class="btn-back" :disabled="stepLoading" @click="step = 2">
                    <v-icon left small>mdi-arrow-left</v-icon> Regresar
                  </v-btn>
                  <v-btn
                    v-if="paymentMethod !== 'ASOCOINS'"
                    depressed
                    class="btn-primary"
                    :loading="stepLoading"
                    :disabled="paymentElementLoading"
                    @click="confirmStripePayment"
                  >
                    <v-icon left small>mdi-lock-outline</v-icon>
                    Pagar ${{ formatAmount(subtotal) }} {{ productService.currency || 'MXN' }}
                  </v-btn>
                  <v-btn
                    v-else
                    depressed
                    class="btn-primary"
                    :loading="asocoinsProcessing"
                    @click="executeAsocoinsPayment"
                  >
                    <v-icon left small>mdi-wallet-giftcard</v-icon>
                    Usar Asocoins
                  </v-btn>
                </div>
              </div>

              <!-- ── STEP 4: Confirmación / Éxito ── -->
              <div v-else-if="step === 4" class="success-state text-center py-8">
                <v-icon size="60" style="color:#9bf4d5" class="mb-4">mdi-check-circle-outline</v-icon>
                <h3 class="success-state__title mb-2">¡Compra registrada!</h3>
                <p class="success-state__text mb-6">{{ successMessage }}</p>
                <v-btn
                  depressed
                  class="btn-primary"
                  to="/member/products"
                >
                  <v-icon left small>mdi-package-variant-closed</v-icon>
                  Ver mis productos
                </v-btn>
              </div>

            </template>
          </div>

          <!-- Card footer -->
          <div class="checkout-footer">
            <v-btn
              text
              small
              :to="step === 4 ? '/member/products' : (productService && productService.slug ? `/p/${productService.slug}` : '/')"
              class="footer-back"
            >
              <v-icon left x-small>mdi-arrow-left</v-icon>
              {{ step === 4 ? 'Ver mis productos' : 'Volver al producto' }}
            </v-btn>
            <v-img
              src="@/assets/images/weChamber/poweredby.svg"
              max-width="90"
              contain
              class="ml-auto"
              style="opacity: 0.45;"
            />
          </div>

        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="asocoinsConfirmDialog" max-width="430">
      <v-card>
        <v-card-title class="headline">Confirmar pago con Asocoins</v-card-title>
        <v-card-text>
          ¿Estás seguro que deseas usar
          <strong>{{ formatAsocoins(asocoinsEligibility && asocoinsEligibility.requiredAmount) }} Asocoins</strong>
          para esta transacción?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="asocoinsConfirmDialog = false">Cancelar</v-btn>
          <v-btn color="#1b273e" dark :loading="asocoinsProcessing" @click="confirmAsocoinsPayment">
            Sí, finalizar compra
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import { mapActions, mapState } from "vuex";
import Endpoints from "@/share/Endpoints";

export default {
  name: "ProductServiceCheckout",
  data() {
    return {
      loading: true,
      stepLoading: false,
      errorMessage: "",
      step: 1,
      stepLabels: ["Detalles", "Tus datos", "Pago"],
      quantity: 1,
      buyer: {
        name: "",
        lastName: "",
        email: "",
        phone: "",
        needsInvoice: false,
      },
      buyerFormValid: false,
      paymentError: "",
      // Stripe Payment Element
      stripeInstance: null,
      elementsInstance: null,
      paymentElementLoading: false,
      paymentIntentId: null,
      paymentMethod: "CREDIT_CARD",
      asocoinsEligibility: null,
      asocoinsConfirmDialog: false,
      asocoinsProcessing: false,
      requiredRule: (v) => !!v || "Campo obligatorio",
      emailRule: (v) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Correo no válido",
    };
  },
  computed: {
    ...mapState("productServiceCheckout", ["productService", "order"]),
    organizationName() {
      const org = this.productService && this.productService.organization;
      if (!org) return "";
      return typeof org === "object" ? org.company || org.name || "" : "";
    },
    subtotal() {
      return (this.productService?.price || 0) * this.quantity;
    },
    showAsocoinsPayment() {
      return Boolean(
        this.asocoinsEligibility &&
          (this.asocoinsEligibility.eligible ||
            this.asocoinsEligibility.reason === "COUPON_NOT_ALLOWED"),
      );
    },
    successMessage() {
      const { redirect_status } = this.$route.query;
      if (redirect_status === "processing")
        return "Tu pago está siendo procesado. Recibirás la confirmación por correo cuando se acredite.";
      const email = this.buyer.email;
      return email
        ? `¡Listo! Recibirás el recibo de compra en ${email}.`
        : "¡Pago completado! Recibirás la confirmación en tu correo electrónico.";
    },
  },
  watch: {
    // Cuando el usuario llega al paso 3, montar el Payment Element
    step(newVal, oldVal) {
      if (newVal === 3 && oldVal !== 3 && this.paymentMethod !== "ASOCOINS") {
        this.$nextTick(() => this.initPaymentElement());
      }
      // Limpiar instancia al salir del paso 3
      if (oldVal === 3 && newVal !== 3) {
        this.stripeInstance = null;
        this.elementsInstance = null;
        this.paymentError = "";
      }
    },
  },
  async mounted() {
    // Regreso desde redirección Stripe (OXXO / SPEI)
    const { payment_intent_client_secret, redirect_status } = this.$route.query;
    if (payment_intent_client_secret) {
      await this.fetchCheckoutInformation(
        this.$route.params.productServiceId,
      ).catch(() => {});
      if (redirect_status === "failed") {
        this.errorMessage =
          "El pago fue rechazado. Intenta de nuevo con otro método.";
      } else {
        this.step = 4;
      }
      this.loading = false;
      return;
    }
    await this.loadCheckout();
  },
  methods: {
    ...mapActions("productServiceCheckout", [
      "fetchCheckoutInformation",
      "createOrder",
      "setQuantity",
      "setPaymentInformation",
    ]),
    formatAmount(val) {
      if (!val && val !== 0) return "0.00";
      return Number(val).toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    async loadCheckout() {
      this.loading = true;
      this.errorMessage = "";
      const productServiceId = this.$route.params.productServiceId;
      const infoRes = await this.fetchCheckoutInformation(productServiceId);
      if (!infoRes.status) {
        this.errorMessage =
          infoRes.message || "No fue posible cargar el producto.";
        this.loading = false;
        return;
      }
      const authUser = this.$store.state.user?.user;
      if (authUser) {
        this.buyer.name = authUser.name || "";
        this.buyer.lastName = authUser.firstLastname || "";
        this.buyer.email = authUser.email || "";
        this.buyer.phone = authUser.phone || "";
      }
      const orderRes = await this.createOrder(productServiceId);
      if (!orderRes.status) {
        this.errorMessage =
          orderRes.message || "No fue posible iniciar la orden.";
      }
      if (orderRes.status) await this.checkAsocoinsEligibility();
      this.loading = false;
    },

    async onConfirmQuantity() {
      if (!this.order) return;
      this.stepLoading = true;
      const res = await this.setQuantity({
        orderId: this.order._id,
        quantity: this.quantity,
      });
      this.stepLoading = false;
      if (!res.status) {
        this.errorMessage = res.message;
        return;
      }
      await this.checkAsocoinsEligibility();
      this.step = 2;
    },

    async onConfirmBuyer() {
      if (!this.$refs.buyerForm.validate()) return;
      await this.checkAsocoinsEligibility();
      this.step = 3;
    },

    async selectPaymentMethod(method) {
      this.paymentMethod = method;
      if (method === "ASOCOINS") {
        this.stripeInstance = null;
        this.elementsInstance = null;
        this.paymentError = "";
        return;
      }
      await this.$nextTick();
      await this.initPaymentElement();
    },

    async checkAsocoinsEligibility() {
      if (!this.order || !this.order._id) return null;
      try {
        const { data } = await this.axios.get(
          Endpoints.asocoinsOrderEligibility.replace(":orderId", this.order._id),
        );
        this.asocoinsEligibility = data;
        if (!data.eligible && this.paymentMethod === "ASOCOINS") {
          this.paymentMethod = "CREDIT_CARD";
        }
        return data;
      } catch (error) {
        this.asocoinsEligibility = null;
        if (this.paymentMethod === "ASOCOINS") this.paymentMethod = "CREDIT_CARD";
        return null;
      }
    },

    formatAsocoins(value) {
      return new Intl.NumberFormat("es-MX").format(Number(value) || 0);
    },

    async executeAsocoinsPayment() {
      const eligibility = await this.checkAsocoinsEligibility();
      if (!eligibility || !eligibility.eligible) {
        this.paymentError = "El saldo ya no es suficiente o la orden no es elegible para Asocoins.";
        return;
      }
      this.asocoinsConfirmDialog = true;
    },

    async confirmAsocoinsPayment() {
      this.asocoinsProcessing = true;
      this.paymentError = "";
      try {
        const { data } = await this.axios.post(
          Endpoints.asocoinsOrderConfirm.replace(":orderId", this.order._id),
        );
        if (data.order) this.$store.commit("productServiceCheckout/SET_ORDER", data.order);
        this.asocoinsConfirmDialog = false;
        this.step = 4;
      } catch (error) {
        this.paymentError =
          error?.response?.data?.error?.message ||
          "No se pudo finalizar el pago con Asocoins.";
      } finally {
        this.asocoinsProcessing = false;
      }
    },

    /** Crea el PaymentIntent en el backend y monta el Payment Element de Stripe */
    async initPaymentElement() {
      this.paymentElementLoading = true;
      this.paymentError = "";
      try {
        const url = Endpoints.productServicePaymentIntent.replace(
          ":orderId",
          this.order._id,
        );
        const { data } = await this.axios({
          method: "POST",
          url,
          data: {
            buyerEmail: this.buyer.email,
            buyerName: `${this.buyer.name} ${this.buyer.lastName}`.trim(),
          },
        });

        const { clientSecret, stripeAccount } = data;

        // Guardar el ID del PaymentIntent para confirmar en el backend después del pago
        this.paymentIntentId = clientSecret.split("_secret_")[0];

        this.stripeInstance = await loadStripe(
          process.env.VUE_APP_STRIPE_API_KEY,
          stripeAccount ? { stripeAccount } : {},
        );

        this.elementsInstance = this.stripeInstance.elements({
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#1b273e",
              colorBackground: "#ffffff",
              colorDanger: "#e53e3e",
              fontFamily: "Inter, system-ui, sans-serif",
              borderRadius: "8px",
              spacingUnit: "4px",
            },
            rules: {
              ".Label": { color: "#4a5568", fontWeight: "500" },
            },
          },
        });

        const paymentElement = this.elementsInstance.create("payment", {
          layout: { type: "accordion", defaultCollapsed: false },
        });

        await this.$nextTick();
        paymentElement.mount(this.$refs.paymentElementContainer);
      } catch (err) {
        this.paymentError =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err.message ||
          "Error al cargar el formulario de pago.";
      } finally {
        this.paymentElementLoading = false;
      }
    },

    /** Confirma el pago con el Payment Element */
    async confirmStripePayment() {
      if (!this.stripeInstance || !this.elementsInstance) {
        this.paymentError = "El formulario de pago no está listo. Intenta de nuevo.";
        return;
      }
      this.stepLoading = true;
      this.paymentError = "";
      try {
        const origin = window.location.origin;
        const productServiceId = this.$route.params.productServiceId;
        const returnUrl = `${origin}/checkout/product-service/${productServiceId}?payment_success=1`;

        const { error } = await this.stripeInstance.confirmPayment({
          elements: this.elementsInstance,
          confirmParams: {
            return_url: returnUrl,
            payment_method_data: {
              billing_details: {
                name: `${this.buyer.name} ${this.buyer.lastName}`.trim() || undefined,
                email: this.buyer.email || undefined,
                phone: this.buyer.phone || undefined,
              },
            },
          },
          // No redirigir si el método no lo requiere (ej. tarjeta 3D-Secure resuelto)
          redirect: "if_required",
        });

        if (error) {
          // Errores de validación o de tarjeta declinada
          this.paymentError = error.message;
          return;
        }

        // Pago exitoso sin redirección (tarjeta)
        // Notificar al backend para marcar la orden como COMPLETED
        try {
          const confirmUrl = Endpoints.productServiceConfirmPayment.replace(
            ":orderId",
            this.order._id,
          );
          await this.axios({
            method: "POST",
            url: confirmUrl,
            data: { paymentIntentId: this.paymentIntentId },
          });
        } catch (confirmErr) {
          // No bloquear al usuario; el webhook de Stripe también completará la orden
          console.warn(
            "Error al confirmar la orden en el backend:",
            confirmErr?.response?.data?.error?.message || confirmErr?.message,
          );
        }
        this.step = 4;
      } catch (err) {
        this.paymentError =
          err?.message || "Error inesperado al procesar el pago.";
      } finally {
        this.stepLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Background ─────────────────────────────────── */
.checkout-bg {
  background: #edf0f5;
}

/* ── Checkout card ───────────────────────────────── */
.checkout-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(27, 39, 62, 0.10), 0 1px 4px rgba(27, 39, 62, 0.06);
  overflow: hidden;
}

/* ── Card header ─────────────────────────────────── */
.checkout-header {
  background: #1b273e;
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.checkout-header__tag {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 5px 0;
}
.checkout-header__title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
}
.checkout-header__org {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 4px;
}
.checkout-header__logo {
  filter: brightness(0) invert(1);
  opacity: 0.9;
}
.checkout-header__secure {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
}

/* ── Step track ──────────────────────────────────── */
.step-track {
  display: flex;
  align-items: center;
  padding: 14px 28px;
  background: #f8f9fc;
  border-bottom: 1px solid #e8ecf0;
}
.step-item {
  display: flex;
  align-items: center;
  gap: 7px;
}
.step-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.step-idle .step-circle {
  background: #e5e8ee;
  color: #9ea8b5;
}
.step-active .step-circle {
  background: #1b273e;
  color: #ffffff;
}
.step-done .step-circle {
  background: #1b273e;
  color: #ffffff;
}
.step-label {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.step-idle .step-label  { color: #9ea8b5; }
.step-active .step-label { color: #1b273e; font-weight: 700; }
.step-done .step-label  { color: #4a5568; }
.step-item--has-connector {
  flex: 1;
}
.step-item--has-connector::after {
  content: '';
  display: block;
  height: 1px;
  background: #e5e8ee;
  margin: 0 8px;
  flex: 1;
}
.step-item--has-connector.step-item--connector-done::after {
  background: #1b273e;
}

/* ── Card body ───────────────────────────────────── */
.checkout-body {
  padding: 28px;
}

/* ── Section titles ──────────────────────────────── */
.section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin: 0;
}
.section-text {
  font-size: 14px;
  color: #4a5568;
  margin: 0;
}

/* ── Product card (step 1) ──────────────────────── */
.product-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #1b273e;
  border-radius: 0 10px 10px 0;
  padding: 14px;
}
.product-card__thumb {
  width: 88px !important;
  height: 88px !important;
  border-radius: 8px;
  background: #e8ecf0;
}
.product-card__thumb--placeholder {
  background: #e8ecf0;
  border-radius: 8px;
}
.product-card__body {
  flex: 1;
  min-width: 0;
}
.product-card__name {
  font-size: 15px;
  font-weight: 700;
  color: #1b273e;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.product-card__desc {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}
.product-card__max-qty {
  font-size: 11px;
  color: #9ca3af;
}
.product-card__pricing {
  text-align: right;
}
.product-card__price-label {
  font-size: 10px;
  color: #9ca3af;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.product-card__price {
  font-size: 22px;
  font-weight: 800;
  color: #1b273e;
  line-height: 1.1;
  white-space: nowrap;
}
.product-card__currency {
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  margin-top: 1px;
}

/* ── Quantity control ────────────────────────────── */
.qty-control {
  display: flex;
  align-items: center;
  gap: 16px;
}
.qty-control__value {
  font-size: 22px;
  font-weight: 700;
  color: #1b273e;
  min-width: 48px;
  text-align: center;
}

/* ── Totals box ──────────────────────────────────── */
.totals-box {
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
}
.totals-box__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
  font-size: 13px;
  color: #4a5568;
}
.totals-box__row:last-child {
  border-bottom: none;
}
.totals-box__row--total {
  background: #f0f4f9;
  font-weight: 700;
  color: #1b273e;
}
.totals-box__amount {
  font-size: 18px;
  font-weight: 800;
  color: #1b273e;
}
.totals-box__currency {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-left: 2px;
}

/* ── Payment method radio ────────────────────────── */
.payment-radio {
  background: #f8f9fc;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 10px 12px;
}
.payment-radio__label {
  font-size: 14px;
  font-weight: 600;
  color: #1b273e;
}
.payment-radio__desc {
  font-size: 12px;
  color: #6b7280;
}

/* ── Stripe Payment Element container ─────────────── */
.payment-element-wrapper {
  min-height: 200px;
}

/* ── Confirm summary ─────────────────────────────── */
.confirm-summary {
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
}
.confirm-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
}
.confirm-summary__row:last-child {
  border-bottom: none;
}
.confirm-summary__row--total {
  background: #f0f4f9;
}
.confirm-summary__label {
  font-size: 13px;
  color: #6b7280;
}
.confirm-summary__value {
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
}
.confirm-summary__total {
  font-size: 22px;
  font-weight: 800;
  color: #1b273e;
}
.confirm-summary__currency {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-left: 2px;
}

/* ── Step actions ────────────────────────────────── */
.step-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.step-actions--end {
  justify-content: flex-end;
}
.btn-primary {
  background: #1b273e !important;
  color: #ffffff !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
  border-radius: 8px;
  padding: 0 22px !important;
  height: 42px !important;
}
.btn-back {
  text-transform: none;
  letter-spacing: 0;
  color: #6b7280 !important;
  font-weight: 500;
}

/* ── Card footer ─────────────────────────────────── */
.checkout-footer {
  background: #f8f9fc;
  border-top: 1px solid #e8ecf0;
  padding: 12px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.footer-back {
  text-transform: none;
  letter-spacing: 0;
  color: #6b7280 !important;
  font-size: 12px;
}

/* ── Success state ───────────────────────────────── */
.success-state__title {
  font-size: 20px;
  font-weight: 700;
  color: #1b273e;
}
.success-state__text {
  font-size: 14px;
  color: #4a5568;
  max-width: 340px;
  margin: 0 auto;
  line-height: 1.6;
}
</style>
